import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Wrench, Zap, Sparkles, Shield, Hammer, Thermometer, 
  Paintbrush, Truck, Trees, CheckCircle, Star, Users,
  FileCheck, AlertTriangle, Menu, X, ChevronRight,
  BadgeCheck, Clock, MessageSquare, Crown, Award, Globe, ArrowRight, Smartphone,
  GraduationCap, Trophy, Flag, Target, ThumbsUp, Timer, TrendingUp, HeartHandshake,
  Lock, MapPin, Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { api } from '../lib/api';

const categories = [
  { name: 'Plumbing', icon: Wrench, color: 'bg-blue-50 text-blue-600' },
  { name: 'Electrical', icon: Zap, color: 'bg-yellow-50 text-yellow-600' },
  { name: 'Cleaning', icon: Sparkles, color: 'bg-purple-50 text-purple-600' },
  { name: 'Security', icon: Shield, color: 'bg-green-50 text-green-600' },
  { name: 'Carpentry', icon: Hammer, color: 'bg-orange-50 text-orange-600' },
  { name: 'AC Repair', icon: Thermometer, color: 'bg-cyan-50 text-cyan-600' },
  { name: 'Painting', icon: Paintbrush, color: 'bg-pink-50 text-pink-600' },
  { name: 'Moving', icon: Truck, color: 'bg-indigo-50 text-indigo-600' },
  { name: 'Landscaping', icon: Trees, color: 'bg-emerald-50 text-emerald-600' },
];

const steps = [
  { 
    step: '01', 
    title: 'Post Your Request', 
    description: 'Describe what you need and when you need it done.',
    icon: MessageSquare
  },
  { 
    step: '02', 
    title: 'Get Matched', 
    description: 'We connect you with verified local professionals instantly.',
    icon: Users
  },
  { 
    step: '03', 
    title: 'Hire & Rate', 
    description: 'Choose the best pro, get the job done, and leave a review.',
    icon: Star
  },
];

const trustFeatures = [
  { title: 'Verified Professionals', description: 'All pros undergo background checks and credential verification.', icon: BadgeCheck },
  { title: 'Ratings & Reviews', description: 'Real reviews from real customers help you make informed decisions.', icon: Star },
  { title: 'Safe Communication', description: 'In-app messaging keeps your personal info private.', icon: Shield },
  { title: 'Issue Resolution', description: 'Our support team is here to help resolve any concerns.', icon: AlertTriangle },
];

const prosBenefits = [
  'Get more jobs in your area',
  'Build your reputation with verified reviews',
  'Set your own schedule and rates',
  'Access to thousands of local customers',
  'Fast, secure payments',
  'Professional tools to manage bookings',
];

function DocBadge({ value }) {
  if (value === '—') {
    return <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-gray-400 font-bold">—</span>;
  }
  const styles = {
    R: 'bg-green-100 text-green-700',
    O: 'bg-orange-100 text-orange-700',
    C: 'bg-blue-100 text-blue-700',
  };
  return (
    <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full font-bold ${styles[value] || 'bg-gray-100 text-gray-400'}`}>
      {value}
    </span>
  );
}

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [featuredPros, setFeaturedPros] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [liveJobs, setLiveJobs] = useState([]);

  useEffect(() => {
    loadFeaturedPros();
    loadLiveJobs();
  }, []);

  // Auto-rotate carousel every 6 seconds
  useEffect(() => {
    if (featuredPros.length <= 3) return;
    const interval = setInterval(() => {
      setCarouselIndex((prev) => {
        const maxIndex = Math.ceil(featuredPros.length / 3) - 1;
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, 6000);
    return () => clearInterval(interval);
  }, [featuredPros.length]);

  const loadFeaturedPros = async () => {
    try {
      const response = await api.getFeaturedProfessionals({ limit: 15 });
      setFeaturedPros(response.data);
    } catch (error) {
      console.error('Failed to load featured professionals:', error);
    }
  };

  const loadLiveJobs = async () => {
    try {
      const response = await api.getJobsPreview(8);
      setLiveJobs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load live jobs:', error);
    }
  };

  const visiblePros = featuredPros.slice(carouselIndex * 3, carouselIndex * 3 + 3);
  const totalPages = Math.max(1, Math.ceil(featuredPros.length / 3));

  const badgeConfig = {
    verified_pro: { icon: Shield, label: 'Verified Pro', color: 'bg-blue-50 text-blue-600 border-blue-200' },
    certified_professional: { icon: GraduationCap, label: 'Certified', color: 'bg-purple-50 text-purple-600 border-purple-200' },
    top_rated: { icon: Star, label: 'Top Rated', color: 'bg-amber-50 text-amber-600 border-amber-200' },
    fast_responder: { icon: Zap, label: 'Fast Responder', color: 'bg-cyan-50 text-cyan-600 border-cyan-200' },
    elite_pro: { icon: Trophy, label: 'Elite Pro', color: 'bg-orange-50 text-orange-600 border-orange-200' },
    guyana_trusted: { icon: Flag, label: 'Guyana Trusted', color: 'bg-green-50 text-green-600 border-green-200' },
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error('Please fill in all fields');
      return;
    }
    setSubmitting(true);
    try {
      await api.submitContact(contactForm);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setContactForm({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-gray-100" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <img src="/logo.png" alt="Link-A-Pro" className="h-10 w-auto" />
              <span className="text-xl font-bold text-[#0F5156] tracking-tight">Link-A-Pro</span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('how-it-works')} className="text-gray-600 hover:text-[#0F5156] transition-colors font-medium" data-testid="nav-features">
                Features
              </button>
              <button onClick={() => scrollToSection('categories')} className="text-gray-600 hover:text-[#0F5156] transition-colors font-medium" data-testid="nav-categories">
                Categories
              </button>
              <button onClick={() => scrollToSection('trust')} className="text-gray-600 hover:text-[#0F5156] transition-colors font-medium" data-testid="nav-trust">
                Trust & Safety
              </button>
              <button onClick={() => scrollToSection('plans')} className="text-gray-600 hover:text-[#0F5156] transition-colors font-medium" data-testid="nav-plans">
                Plans
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-600 hover:text-[#0F5156] transition-colors font-medium" data-testid="nav-contact">
                Contact
              </button>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <a href="https://app.link-a-pro.com/login" target="_blank" rel="noopener noreferrer" className="text-[#0F5156] hover:text-[#0A3A3E] font-medium" data-testid="admin-login-link">
                Admin
              </a>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 py-4 px-6" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <button onClick={() => scrollToSection('how-it-works')} className="text-left text-gray-600 hover:text-[#0F5156] font-medium">Features</button>
              <button onClick={() => scrollToSection('categories')} className="text-left text-gray-600 hover:text-[#0F5156] font-medium">Categories</button>
              <button onClick={() => scrollToSection('trust')} className="text-left text-gray-600 hover:text-[#0F5156] font-medium">Trust & Safety</button>
              <button onClick={() => scrollToSection('plans')} className="text-left text-gray-600 hover:text-[#0F5156] font-medium">Plans</button>
              <button onClick={() => scrollToSection('contact')} className="text-left text-gray-600 hover:text-[#0F5156] font-medium">Contact</button>
              <a href="https://app.link-a-pro.com/login" target="_blank" rel="noopener noreferrer" className="text-[#0F5156] font-medium">Admin Login</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 hero-gradient" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slideUp">
              <span className="inline-block px-4 py-2 bg-[#0F5156]/10 text-[#0F5156] rounded-full text-sm font-medium mb-6">
                Guyana's newest way to find reliable professionals — get early access
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight leading-tight mb-6">
                Guyana's First Marketplace for <span className="text-[#0F5156]">Trusted Home Service Pros</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
                Skip the guesswork. Find vetted, reliable professionals near you — and get the job done right.
              </p>
              
              {/* PWA Download Button */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a 
                  href="https://app.link-a-pro.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-[#0F5156] text-white px-8 py-4 rounded-xl hover:bg-[#0A3A3E] transition-all duration-300 hover:-translate-y-1 shadow-lg shadow-[#0F5156]/25"
                  data-testid="launch-app-btn"
                >
                  <Smartphone className="w-6 h-6" />
                  <div className="text-left">
                    <div className="text-xs opacity-80">No download needed</div>
                    <div className="text-base font-semibold">Find a Pro Today</div>
                  </div>
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
                <a 
                  href="https://app.link-a-pro.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-white text-[#0F5156] border-2 border-[#0F5156] px-8 py-4 rounded-xl hover:bg-[#0F5156]/5 transition-all duration-300 hover:-translate-y-1"
                  data-testid="join-pro-btn"
                >
                  <Crown className="w-6 h-6" />
                  <div className="text-left">
                    <div className="text-xs opacity-70">Grow your business</div>
                    <div className="text-base font-semibold">Join as a Professional</div>
                  </div>
                </a>
              </div>

              <div className="flex items-center gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  <span>Works on any device</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Free to use</span>
                </div>
              </div>
            </div>

            <div className="relative animate-slideInRight hidden lg:block">
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&h=700&fit=crop" 
                  alt="Professional worker"
                  className="rounded-3xl shadow-2xl w-full object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Verified Pro</div>
                      <div className="text-sm text-gray-500">Background Checked</div>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    <span className="font-bold text-gray-900">4.9</span>
                    <span className="text-gray-500 text-sm">(2.5k reviews)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Jobs Ticker — privacy-safe preview of recent open jobs */}
      {liveJobs.length > 0 && (
        <section id="live-jobs" className="py-16 md:py-20 bg-white border-y border-gray-100" data-testid="live-jobs-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0FB9B1] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-[#0FB9B1]"></span>
                  </span>
                  <span className="text-[#0FB9B1] font-semibold text-sm tracking-wider uppercase">Live Demand</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                  Live jobs on Link-A-Pro right now
                </h2>
                <p className="text-gray-600 max-w-2xl">
                  Real customers, real requests. Sign in to view full details and respond — we keep customer information private until you're verified.
                </p>
              </div>
              <a
                href="https://app.link-a-pro.com/register?intent=respond_to_job"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#0F5156] text-white px-5 py-3 rounded-xl hover:bg-[#0A3A3E] transition-all duration-300 shadow-md self-start md:self-end"
                data-testid="live-jobs-cta"
              >
                <Crown className="w-4 h-4" />
                <span className="font-semibold text-sm">Join as a Pro</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {liveJobs.map((job) => (
                <a
                  key={job.id_short}
                  href={`https://app.link-a-pro.com/register?intent=respond_to_job&job=${encodeURIComponent(job.id_short)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white border border-gray-200 rounded-2xl p-5 hover:border-[#0FB9B1] hover:shadow-lg transition-all duration-300 flex flex-col"
                  data-testid={`live-job-${job.id_short}`}
                >
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <span className="inline-flex items-center gap-1 bg-[#0F5156]/10 text-[#0F5156] text-xs font-semibold px-2.5 py-1 rounded-full">
                      <Wrench className="w-3 h-3" /> {job.category}
                    </span>
                    <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full">
                      <MapPin className="w-3 h-3" /> {job.region}
                    </span>
                  </div>

                  <h4 className="font-bold text-gray-900 text-base mb-2 line-clamp-2">
                    {job.title_preview}
                  </h4>
                  {job.description_preview && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                      {job.description_preview}
                    </p>
                  )}

                  <div className="mt-auto pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-3">
                      <span className="font-semibold text-gray-900">{job.budget_range}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {job.posted_ago}</span>
                    </div>
                    <div className="flex items-center justify-center gap-1.5 bg-gray-50 group-hover:bg-[#0F5156] group-hover:text-white text-gray-700 text-xs font-semibold py-2 rounded-lg transition-colors">
                      <Lock className="w-3 h-3" />
                      <span>Sign in to respond</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>

            <p className="text-center text-xs text-gray-500 mt-8 flex items-center justify-center gap-1.5">
              <Shield className="w-3 h-3" />
              Customer names, contact details, and full job descriptions are protected until you sign in.
            </p>
          </div>
        </section>
      )}


      {/* How It Works */}
      <section id="how-it-works" className="py-20 md:py-32 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm tracking-wider uppercase">How It Works</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-4">
              Get Help in 3 Simple Steps
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Finding the right professional has never been easier
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((item, index) => (
              <div 
                key={item.step}
                className="relative card-marketing text-center group"
                data-testid={`step-${index + 1}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#0F5156]/10 flex items-center justify-center group-hover:bg-[#0F5156] transition-colors duration-300">
                  <item.icon className="w-8 h-8 text-[#0F5156] group-hover:text-white transition-colors duration-300" />
                </div>
                <div className="text-6xl font-bold text-gray-100 absolute top-4 left-4">{item.step}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section id="categories" className="py-20 md:py-32 bg-[#F5F2EB]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm tracking-wider uppercase">Services</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-4">
              Browse by Category
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Find the right professional for any job around your home
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
            {categories.map((category, index) => (
              <div 
                key={category.name}
                className="bg-white rounded-2xl p-6 text-center cursor-pointer hover:shadow-lg transition-all duration-300 hover:-translate-y-2 group"
                data-testid={`category-${category.name.toLowerCase().replace(' ', '-')}`}
              >
                <div className={`w-14 h-14 mx-auto mb-4 rounded-xl ${category.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                  <category.icon className="w-7 h-7" />
                </div>
                <h3 className="font-semibold text-gray-900">{category.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Professionals — Auto-Rotating Carousel */}
      {featuredPros.length > 0 && (
        <section id="featured-pros" className="py-20 md:py-32 bg-white" data-testid="featured-pros-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-[#E07A5F] font-semibold text-sm tracking-wider uppercase flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" />
                Featured Pros
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-4">
                Top-Rated Professionals
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Meet our highest-rated and most trusted professionals ready to help
              </p>
            </div>

            {/* Carousel */}
            <div className="relative">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-500">
                {visiblePros.map((pro) => (
                  <a 
                    key={pro.id}
                    href="https://app.link-a-pro.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 relative overflow-hidden group block"
                    data-testid={`featured-pro-${pro.id}`}
                  >
                    {/* Admin Featured Indicator */}
                    {pro.is_admin_featured && (
                      <div className="absolute top-4 right-4">
                        <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 border border-amber-200 text-xs font-medium rounded-full px-2.5 py-1">
                          <Sparkles className="w-3 h-3" />
                          Featured
                        </span>
                      </div>
                    )}

                    {/* Profile */}
                    <div className="flex items-center gap-4 mt-2">
                      <div className="w-16 h-16 rounded-full flex-shrink-0 overflow-hidden">
                        {pro.profile_image ? (
                          <img src={pro.profile_image} alt={pro.name} className="w-16 h-16 object-cover rounded-full" />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0F5156] to-[#0F5156]/70 flex items-center justify-center">
                            <span className="text-2xl font-bold text-white">{(pro.name || '?').charAt(0)}</span>
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold text-gray-900 text-lg truncate">{pro.name}</h3>
                        <p className="text-[#0F5156] font-medium text-sm">{pro.category}</p>
                      </div>
                    </div>

                    {/* Rating */}
                    <div className="mt-4 flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-4 h-4 ${i < Math.round(pro.rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} 
                          />
                        ))}
                      </div>
                      <span className="font-bold text-gray-900 text-sm">{(pro.rating || 0).toFixed(1)}</span>
                      <span className="text-gray-400 text-sm">({pro.total_reviews || 0} reviews)</span>
                    </div>

                    {/* Earned Badges */}
                    {pro.badges && pro.badges.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {pro.badges.map((badge) => {
                          const config = badgeConfig[badge];
                          if (!config) return null;
                          const BadgeIcon = config.icon;
                          return (
                            <span 
                              key={badge} 
                              className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-2.5 py-1 border ${config.color}`}
                            >
                              <BadgeIcon className="w-3 h-3" />
                              {config.label}
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                      <span className="text-sm text-gray-500">{pro.total_jobs || 0} jobs completed</span>
                      <span className="text-sm text-[#0F5156] font-medium group-hover:underline flex items-center gap-1">
                        View Profile <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </a>
                ))}
              </div>

              {/* Carousel Dots */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCarouselIndex(i)}
                      className={`h-2.5 rounded-full transition-all duration-300 ${
                        i === carouselIndex 
                          ? 'bg-[#0F5156] w-8' 
                          : 'bg-gray-300 w-2.5 hover:bg-gray-400'
                      }`}
                      aria-label={`Go to page ${i + 1}`}
                      data-testid={`carousel-dot-${i}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Trust Is Earned. Never Bought. */}
      <section id="trust" className="py-20 md:py-32 bg-white" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-[#E07A5F] font-semibold text-sm tracking-wider uppercase flex items-center justify-center gap-2">
              <Shield className="w-4 h-4" />
              Trust & Safety
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Trust Is Earned. Never Bought.
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Link-A-Pro was built to solve one of Guyana's biggest challenges: finding professionals you can genuinely trust.
            </p>
          </div>

          {/* The Problem */}
          <div className="max-w-4xl mx-auto mb-20">
            <div className="bg-gray-50 rounded-3xl p-8 md:p-12 border border-gray-100">
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6">
                For years, finding a reliable contractor, plumber, electrician, mechanic, cleaner, carpenter, photographer, or other service provider has often come down to word of mouth, Facebook posts, WhatsApp groups, and luck.
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6">
                Too often, people hire someone based on a recommendation only to discover poor workmanship, missed deadlines, lack of professionalism, or unfinished jobs.
              </p>
              <p className="text-[#0F5156] text-lg md:text-xl font-semibold">
                Link-A-Pro was created to change that.
              </p>
            </div>
          </div>

          {/* Core Pillars */}
          <div className="grid md:grid-cols-3 gap-6 mb-20">
            <div className="text-center p-8 rounded-2xl bg-[#0F5156]/5 border border-[#0F5156]/10">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-[#0F5156] flex items-center justify-center">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">Transparency</h3>
              <p className="text-gray-600 text-sm">Every professional earns their reputation through real customer experiences.</p>
            </div>
            <div className="text-center p-8 rounded-2xl bg-[#0F5156]/5 border border-[#0F5156]/10">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-[#0F5156] flex items-center justify-center">
                <ThumbsUp className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">Accountability</h3>
              <p className="text-gray-600 text-sm">Rankings are based on performance, not payment. Quality work is rewarded.</p>
            </div>
            <div className="text-center p-8 rounded-2xl bg-[#0F5156]/5 border border-[#0F5156]/10">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-[#0F5156] flex items-center justify-center">
                <HeartHandshake className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">Community Trust</h3>
              <p className="text-gray-600 text-sm">The community decides who the best professionals are — not advertising dollars.</p>
            </div>
          </div>

          {/* No Pay-to-Rank */}
          <div className="mb-20">
            <div className="bg-[#0F5156] rounded-3xl p-8 md:p-12 text-white">
              <div className="max-w-3xl mx-auto text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-white/10 flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-[#E07A5F]" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-6">
                  NO PAY-TO-RANK SYSTEM
                </h3>
                <p className="text-lg opacity-90 mb-8">
                  Unlike many directories and advertising platforms, professionals cannot pay Link-A-Pro to receive a higher ranking. Let us say that again:
                </p>
                <div className="space-y-4 text-left max-w-lg mx-auto">
                  <div className="flex items-center gap-4 bg-white/10 rounded-xl p-4">
                    <X className="w-6 h-6 text-[#E07A5F] flex-shrink-0" />
                    <span className="font-semibold text-lg">Professionals cannot buy better ratings.</span>
                  </div>
                  <div className="flex items-center gap-4 bg-white/10 rounded-xl p-4">
                    <X className="w-6 h-6 text-[#E07A5F] flex-shrink-0" />
                    <span className="font-semibold text-lg">Professionals cannot buy higher rankings.</span>
                  </div>
                  <div className="flex items-center gap-4 bg-white/10 rounded-xl p-4">
                    <X className="w-6 h-6 text-[#E07A5F] flex-shrink-0" />
                    <span className="font-semibold text-lg">Professionals cannot pay to appear as a Top Rated Pro.</span>
                  </div>
                </div>
                <p className="mt-8 text-lg font-medium opacity-90">
                  Every ranking is determined independently using verified customer feedback, job performance, response times, and service quality.
                </p>
                <p className="mt-4 text-2xl font-bold text-[#E07A5F]">
                  Trust must be earned.
                </p>
              </div>
            </div>
          </div>

          {/* People-Powered Ratings */}
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <span className="text-[#E07A5F] font-semibold text-sm tracking-wider uppercase">People-Powered</span>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mt-3 mb-6">
                People-Powered Ratings
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                The professionals who rise to the top are the ones who consistently deliver outstanding service to their customers. Our ranking system is powered by:
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Star, label: 'Customer Ratings' },
                  { icon: CheckCircle, label: 'Verified Reviews' },
                  { icon: FileCheck, label: 'Job Completion History' },
                  { icon: Timer, label: 'Response Times' },
                  { icon: TrendingUp, label: 'Reliability Metrics' },
                  { icon: Trophy, label: 'Long-Term Performance' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3 bg-amber-50 rounded-xl p-4 border border-amber-100">
                    <item.icon className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <span className="font-medium text-gray-800">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#0F5156]/5 to-[#E07A5F]/5 rounded-3xl p-8 md:p-10 border border-gray-100">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                  <Users className="w-10 h-10 text-amber-600" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Community Decides</h4>
                <p className="text-gray-600">
                  The community decides who the best professionals are — not advertising dollars.
                </p>
              </div>
            </div>
          </div>

          {/* Verified Badges */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <span className="text-[#E07A5F] font-semibold text-sm tracking-wider uppercase">The Badge System</span>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mt-3 mb-4">
                Every Badge Tells a Story
              </h3>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Link-A-Pro distinguishes between <strong>earned badges</strong> — which reflect verified performance — and <strong>subscriber badges</strong>, which only signal an active paid plan. <span className="text-[#0F5156] font-semibold">Subscriber badges never influence trust scores, search rankings, or customer ratings.</span>
              </p>
            </div>

            {/* EARNED BADGES */}
            <div className="mb-12">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Target className="w-6 h-6 text-[#0F5156]" />
                <h4 className="text-xl md:text-2xl font-bold text-gray-900">Earned Badges</h4>
              </div>
              <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
                Can never be bought. Awarded only through verified identity, real customer reviews, and proven performance.
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    icon: Shield,
                    color: 'bg-blue-50 text-blue-600 border-blue-100',
                    title: 'Verified Pro',
                    desc: 'Identity and contact information verified by Link-A-Pro admins. The baseline trust badge.',
                    criteria: 'Identity + contact verified',
                  },
                  {
                    icon: GraduationCap,
                    color: 'bg-purple-50 text-purple-600 border-purple-100',
                    title: 'Certified Professional',
                    desc: 'Professional licenses and certifications (trade license, insurance, etc.) submitted and verified.',
                    criteria: 'Licenses & certifications verified',
                  },
                  {
                    icon: Star,
                    color: 'bg-amber-50 text-amber-600 border-amber-100',
                    title: 'Top Rated Pro',
                    desc: 'Consistently exceptional ratings — requires all four thresholds below.',
                    criteria: '10+ jobs · 10+ reviews · 4.5★ · trust ≥ 85',
                  },
                  {
                    icon: Zap,
                    color: 'bg-cyan-50 text-cyan-600 border-cyan-100',
                    title: 'Fast Responder',
                    desc: 'Average response time ≤ 1 hour, measured automatically from message timestamps.',
                    criteria: '≤ 1 hr avg · 10+ conversations',
                  },
                  {
                    icon: Flag,
                    color: 'bg-green-50 text-green-600 border-green-100',
                    title: 'Guyana Trusted',
                    desc: 'The elder-statesman badge — long-term reliability with a flawless dispute record.',
                    criteria: '12+ mo · 25+ jobs · 20+ reviews · trust ≥ 90 · 0 unresolved disputes',
                  },
                  {
                    icon: Trophy,
                    color: 'bg-orange-50 text-orange-600 border-orange-100',
                    title: 'Elite Pro',
                    desc: 'Top 5% of professionals in their service category by Trust Score. Awarded automatically — cannot be applied for or purchased.',
                    criteria: 'Top 5% by Trust Score in category',
                  },
                ].map((badge) => (
                  <div
                    key={badge.title}
                    className={`rounded-2xl p-6 border ${badge.color.split(' ')[0]} ${badge.color.split(' ').slice(2).join(' ')} hover:shadow-lg transition-all duration-300`}
                    data-testid={`badge-${badge.title.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div className={`w-12 h-12 rounded-xl ${badge.color.split(' ')[0]} flex items-center justify-center mb-4`}>
                      <badge.icon className={`w-6 h-6 ${badge.color.split(' ')[1]}`} />
                    </div>
                    <h4 className="font-bold text-gray-900 text-lg mb-2">{badge.title}</h4>
                    <p className="text-gray-600 text-sm mb-3">{badge.desc}</p>
                    <div className="text-xs font-semibold text-gray-700 bg-white/60 rounded-lg px-3 py-2 border border-gray-200">
                      <span className="text-[#0F5156]">How it's earned:</span> {badge.criteria}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* SUBSCRIBER BADGES */}
            <div>
              <div className="flex items-center justify-center gap-3 mb-2">
                <Crown className="w-6 h-6 text-[#FFC312]" />
                <h4 className="text-xl md:text-2xl font-bold text-gray-900">Subscriber Badges</h4>
              </div>
              <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
                Identify professionals who subscribe to paid plans for additional business tools. <strong className="text-gray-900">Never influence ranking, rating, or search.</strong>
              </p>
              <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {[
                  {
                    icon: Award,
                    color: 'bg-teal-50 text-teal-600 border-teal-100',
                    title: 'Pro Plus',
                    desc: 'Active Pro plan subscriber — the pro has invested in extra business tools (logo, website, social links, advanced analytics).',
                  },
                  {
                    icon: Sparkles,
                    color: 'bg-yellow-50 text-yellow-600 border-yellow-100',
                    title: 'Elite Member',
                    desc: 'Active Elite plan subscriber — premium perks like custom branding, unlimited portfolio, video, multi-location, before/after galleries, and homepage featured placement.',
                  },
                ].map((badge) => (
                  <div
                    key={badge.title}
                    className={`rounded-2xl p-6 border ${badge.color.split(' ')[0]} ${badge.color.split(' ').slice(2).join(' ')} hover:shadow-lg transition-all duration-300`}
                    data-testid={`subscriber-badge-${badge.title.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex-shrink-0 ${badge.color.split(' ')[0]} flex items-center justify-center`}>
                        <badge.icon className={`w-6 h-6 ${badge.color.split(' ')[1]}`} />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-lg mb-2">{badge.title}</h4>
                        <p className="text-gray-600 text-sm">{badge.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* HOW WE SCORE & RANK */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <span className="text-[#E07A5F] font-semibold text-sm tracking-wider uppercase">Transparent by Design</span>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mt-3 mb-4">
                How We Score &amp; Rank — Publicly
              </h3>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                No secret algorithms. Trust Scores are computed from verified reviews, and search rankings use only performance signals — never the subscription plan.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Trust Score Formula */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8" data-testid="trust-score-formula">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#0F5156]/10 flex items-center justify-center">
                    <BadgeCheck className="w-5 h-5 text-[#0F5156]" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Trust Score Formula (0–100)</h4>
                </div>
                <p className="text-sm text-gray-600 mb-5">
                  Computed from verified reviews only. Each review contributes a weighted average across these dimensions:
                </p>
                <div className="space-y-3">
                  {[
                    { label: 'Overall rating', weight: 40 },
                    { label: 'Quality of work', weight: 20 },
                    { label: 'Professionalism', weight: 15 },
                    { label: 'Timeliness', weight: 10 },
                    { label: 'Communication', weight: 10 },
                    { label: 'Repeat-hire likelihood', weight: 5 },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center gap-3">
                      <div className="w-16 text-right font-bold text-[#0F5156] text-sm">{row.weight}%</div>
                      <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div className="h-full bg-[#0F5156] rounded-full" style={{ width: `${row.weight * 2.5}%` }}></div>
                      </div>
                      <div className="w-44 text-sm text-gray-700 font-medium">{row.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ranking Signals */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8" data-testid="ranking-signals">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#FFC312]/15 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-[#B8860B]" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">Search Ranking Signals</h4>
                </div>
                <p className="text-sm text-gray-600 mb-5">
                  Search and category ordering are determined <strong>only</strong> by these performance signals — never by subscription plan:
                </p>
                <div className="space-y-3">
                  {[
                    { icon: Shield, label: 'Trust Score' },
                    { icon: Star, label: 'Verified Reviews Count' },
                    { icon: CheckCircle, label: 'Completed Jobs Count' },
                    { icon: ThumbsUp, label: 'Customer Recommendation Rate' },
                    { icon: Timer, label: 'Response Time' },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                      <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center">
                        <row.icon className="w-4 h-4 text-[#0F5156]" />
                      </div>
                      <span className="font-medium text-gray-800">{row.label}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <p className="text-sm text-gray-700 italic">
                    "Trust is earned, never bought."
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Our Promise to Guyana */}
          <div className="mb-20">
            <div className="bg-gradient-to-br from-gray-50 to-[#0F5156]/5 rounded-3xl p-8 md:p-12 border border-gray-100">
              <div className="max-w-3xl mx-auto text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#0F5156]/10 flex items-center justify-center">
                  <Flag className="w-8 h-8 text-[#0F5156]" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                  Our Promise to Guyana
                </h3>
                <p className="text-lg text-gray-600 mb-8">
                  We believe every Guyanese family, homeowner, business owner, and organization deserves a safe and trustworthy way to find qualified professionals. That's why our mission is simple:
                </p>
                <div className="grid sm:grid-cols-2 gap-4 max-w-lg mx-auto mb-8">
                  {['Reward quality.', 'Reward honesty.', 'Reward professionalism.', 'Give customers the information they need to hire with confidence.'].map((promise) => (
                    <div key={promise} className="flex items-center gap-3 text-left">
                      <CheckCircle className="w-5 h-5 text-[#0F5156] flex-shrink-0" />
                      <span className="font-medium text-gray-800">{promise}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xl font-bold text-[#0F5156]">
                  Because trust should never be for sale.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Find Trusted Professionals Across Guyana
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Join the growing community of customers and professionals helping build a more trustworthy service marketplace for Guyana.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://app.link-a-pro.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-[#0F5156] text-white px-8 py-4 rounded-xl hover:bg-[#0A3A3E] transition-all duration-300 hover:-translate-y-1 shadow-lg shadow-[#0F5156]/25"
                data-testid="trust-cta-app"
              >
                <Smartphone className="w-5 h-5" />
                <span className="font-semibold">Find a Professional</span>
                <ArrowRight className="w-5 h-5" />
              </a>
              <a 
                href="https://app.link-a-pro.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-white text-[#0F5156] border-2 border-[#0F5156] px-8 py-4 rounded-xl hover:bg-[#0F5156]/5 transition-all duration-300 hover:-translate-y-1"
                data-testid="trust-cta-join"
              >
                <Crown className="w-5 h-5" />
                <span className="font-semibold">Join as a Professional</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* For Professionals */}
      <section id="professionals" className="py-20 md:py-32 bg-[#0F5156]" data-testid="professionals-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=600&h=500&fit=crop" 
                alt="Professional at work"
                className="rounded-3xl shadow-xl"
              />
              <div className="absolute -top-4 -right-4 bg-[#E07A5F] text-white rounded-2xl p-4 shadow-xl animate-pulse">
                <div className="text-center">
                  <div className="text-xs font-medium opacity-90">LIMITED TIME</div>
                  <div className="text-xl font-bold">2-3 Months</div>
                  <div className="text-sm font-semibold">FREE Pro Plan!</div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2 text-white">
              <span className="text-[#E07A5F] font-semibold text-sm tracking-wider uppercase">For Professionals</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-6">
                Grow Your Business with Link-A-Pro
              </h2>
              <p className="text-lg opacity-90 mb-6">
                Join thousands of skilled professionals who are getting more jobs and building their reputation on our platform.
              </p>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/20" data-testid="free-pro-offer">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E07A5F] flex items-center justify-center">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-xl">Get 2-3 Months FREE</div>
                    <div className="text-sm opacity-80">Pro Plan (Worth $24.99/month)</div>
                  </div>
                </div>
                <p className="text-sm opacity-90 mb-4">
                  Sign up now and receive complimentary access to our Pro Plan features — no credit card required.
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {prosBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3" data-testid={`pro-benefit-${index}`}>
                    <CheckCircle className="w-5 h-5 text-[#E07A5F] flex-shrink-0" />
                    <span className="text-sm opacity-90">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://app.link-a-pro.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#E07A5F] hover:bg-[#D66A4F] text-white rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 hover:-translate-y-1"
                  data-testid="become-pro-btn"
                >
                  <Crown className="w-5 h-5" />
                  Start Your Free Pro Trial
                  <ArrowRight className="w-5 h-5" />
                </a>
                <button 
                  onClick={() => scrollToSection('plans')}
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 border border-white/20"
                  data-testid="view-plans-btn"
                >
                  View All Plans
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Plans Section */}
      <section id="plans" className="py-20 md:py-32 bg-gray-50" data-testid="plans-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="text-center mb-6">
            <span className="text-[#E07A5F] font-semibold text-sm tracking-wider uppercase flex items-center justify-center gap-2">
              <Crown className="w-4 h-4" />
              Professional Plans
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Link-A-Pro Professional Plans
            </h2>
          </div>

          {/* Trust Disclaimer */}
          <div className="max-w-3xl mx-auto mb-16">
            <div className="bg-[#0F5156]/5 border border-[#0F5156]/15 rounded-2xl p-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-[#0F5156]" />
                <span className="font-bold text-[#0F5156] text-sm uppercase tracking-wider">Trust Is Earned. Never Bought.</span>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                All Link-A-Pro professionals are ranked using independently calculated trust and performance metrics. Paid plans provide additional business tools, branding opportunities, and visibility features, but they <strong className="text-gray-900">NEVER</strong> influence Trust Score, customer ratings, verified reviews, Top Rated status, Guyana Trusted status, or Elite Pro status.
              </p>
            </div>
          </div>

          {/* Plan Cards */}
          <div className="grid lg:grid-cols-3 gap-8 mb-20">
            
            {/* Starter Plan */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid="plan-starter">
              <div className="p-8">
                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Starter Plan</div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-bold text-gray-900">$9.99</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <div className="text-sm text-gray-400 mb-6">Approx. GYD $2,100/month</div>
                <p className="text-gray-600 text-sm mb-6">
                  Perfect for professionals who want to establish credibility, collect reviews, and begin growing their business.
                </p>
                <a 
                  href="https://app.link-a-pro.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-gray-900 hover:bg-gray-800 text-white rounded-xl py-3 font-medium transition-colors"
                  data-testid="plan-starter-cta"
                >
                  Get Started
                </a>
              </div>
              <div className="border-t border-gray-100 p-8">
                <div className="text-sm font-semibold text-gray-900 mb-4">Included:</div>
                <div className="space-y-3">
                  {[
                    'Professional Business Profile',
                    'Receive Unlimited Customer Inquiries',
                    'In-App Messaging',
                    'Portfolio Gallery (Up to 10 Photos)',
                    'Service Categories',
                    'Service Area Coverage',
                    'Basic Analytics Dashboard',
                    'Email Support',
                    'Priority Listing Presentation',
                    'Verified Customer Reviews',
                    'Eligibility for Verified Pro Badge',
                    'Eligibility for Certified Professional Badge',
                    'Eligibility for Top Rated Pro Badge',
                    'Eligibility for Guyana Trusted Badge',
                    'Eligibility for Elite Pro Badge',
                    'Trust Score Display',
                    'Customer Recommendation Tracking',
                  ].map((feature) => (
                    <div key={feature} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Best For:</div>
                  <p className="text-sm text-gray-600">Individual professionals, freelancers, tradesmen, and businesses starting their journey on Link-A-Pro.</p>
                </div>
              </div>
            </div>

            {/* Pro Plan */}
            <div className="bg-white rounded-3xl shadow-lg border-2 border-[#E07A5F] overflow-hidden relative hover:shadow-xl transition-shadow" data-testid="plan-pro">
              <div className="bg-[#E07A5F] text-white text-center py-2 text-sm font-bold uppercase tracking-wider">
                Most Popular
              </div>
              <div className="p-8">
                <div className="text-sm font-semibold text-[#E07A5F] uppercase tracking-wider mb-2">Pro Plan</div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-bold text-gray-900">$24.99</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <div className="text-sm text-gray-400 mb-6">Approx. GYD $5,250/month</div>
                <p className="text-gray-600 text-sm mb-6">
                  Designed for professionals who actively rely on Link-A-Pro to generate leads and grow their customer base.
                </p>
                <a 
                  href="https://app.link-a-pro.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-[#E07A5F] hover:bg-[#D66A4F] text-white rounded-xl py-3 font-medium transition-colors"
                  data-testid="plan-pro-cta"
                >
                  Start Free Trial
                </a>
              </div>
              <div className="border-t border-gray-100 p-8">
                <div className="text-sm font-semibold text-gray-900 mb-4">Everything in Starter PLUS:</div>
                <div className="space-y-3">
                  {[
                    'Featured Pro Plus Badge',
                    'Highlighted Profile Card',
                    'Highlighted Category Listings',
                    'Up to 50 Portfolio Photos',
                    'Business Logo Display',
                    'Website Link',
                    'Social Media Links',
                    'Monthly Performance Report',
                    'Lead Tracking Dashboard',
                    'Customer Inquiry Analytics',
                    'Service Area Mapping',
                    'Job Performance Dashboard',
                    'Enhanced Business Profile',
                    'Priority Customer Support',
                    'Profile Performance Insights',
                  ].map((feature) => (
                    <div key={feature} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#E07A5F] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Best For:</div>
                  <p className="text-sm text-gray-600">Growing contractors, electricians, plumbers, cleaners, landscapers, mechanics, photographers, and service businesses seeking increased visibility and business intelligence.</p>
                </div>
              </div>
            </div>

            {/* Elite Plan */}
            <div className="bg-white rounded-3xl shadow-sm border border-amber-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid="plan-elite">
              <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-center py-2 text-sm font-bold uppercase tracking-wider">
                Maximum Growth
              </div>
              <div className="p-8">
                <div className="text-sm font-semibold text-amber-600 uppercase tracking-wider mb-2">Elite Plan</div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-bold text-gray-900">$49.99</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <div className="text-sm text-gray-400 mb-6">Approx. GYD $10,500/month</div>
                <p className="text-gray-600 text-sm mb-6">
                  Built for established professionals and companies that want maximum exposure and advanced business growth tools.
                </p>
                <a 
                  href="https://app.link-a-pro.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl py-3 font-medium transition-colors"
                  data-testid="plan-elite-cta"
                >
                  Go Elite
                </a>
              </div>
              <div className="border-t border-gray-100 p-8">
                <div className="text-sm font-semibold text-gray-900 mb-4">Everything in Pro PLUS:</div>
                <div className="space-y-3">
                  {[
                    'Homepage Featured Placement',
                    'Premium Pro Plus Elite Badge',
                    'Custom Profile Branding',
                    'Unlimited Portfolio Photos',
                    'Video Portfolio Uploads',
                    'Before & After Project Galleries',
                    'Advanced Business Analytics',
                    'Customer Trend Reports',
                    'Competitor Benchmark Insights',
                    'Dedicated Account Manager',
                    'Social Media Promotion Opportunities',
                    'Featured Business Showcase',
                    'Multi-Location Business Profiles',
                    'Early Access to New Platform Features',
                    'Premium Customer Support',
                    'Custom Business Performance Reports',
                  ].map((feature) => (
                    <div key={feature} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Best For:</div>
                  <p className="text-sm text-gray-600">Established contractors, construction companies, engineering firms, cleaning companies, maintenance companies, and service businesses looking to dominate their market.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="mb-20" data-testid="plan-comparison">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">Plan Comparison</h3>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-4 font-semibold text-gray-900 bg-gray-50 w-2/5">Feature</th>
                    <th className="text-center p-4 font-semibold text-gray-900 bg-gray-50">Starter</th>
                    <th className="text-center p-4 font-semibold text-[#E07A5F] bg-[#E07A5F]/5">Pro</th>
                    <th className="text-center p-4 font-semibold text-amber-600 bg-amber-50">Elite</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: 'Monthly Price (USD)', starter: '$9.99', pro: '$24.99', elite: '$49.99' },
                    { feature: 'Monthly Price (GYD)', starter: '~$2,100', pro: '~$5,250', elite: '~$10,500' },
                    { feature: 'Business Profile', starter: true, pro: 'Enhanced', elite: 'Custom Branded' },
                    { feature: 'Customer Inquiries', starter: 'Unlimited', pro: 'Unlimited', elite: 'Unlimited' },
                    { feature: 'In-App Messaging', starter: true, pro: true, elite: true },
                    { feature: 'Portfolio Photos', starter: '10', pro: '50', elite: 'Unlimited' },
                    { feature: 'Video Portfolio', starter: false, pro: false, elite: true },
                    { feature: 'Before & After Galleries', starter: false, pro: false, elite: true },
                    { feature: 'Business Logo Display', starter: false, pro: true, elite: true },
                    { feature: 'Website & Social Links', starter: false, pro: true, elite: true },
                    { feature: 'Analytics Dashboard', starter: 'Basic', pro: 'Advanced', elite: 'Premium' },
                    { feature: 'Performance Reports', starter: false, pro: 'Monthly', elite: 'Custom' },
                    { feature: 'Customer Trend Reports', starter: false, pro: false, elite: true },
                    { feature: 'Competitor Benchmarks', starter: false, pro: false, elite: true },
                    { feature: 'Highlighted Profile Card', starter: false, pro: true, elite: true },
                    { feature: 'Homepage Featured Placement', starter: false, pro: false, elite: true },
                    { feature: 'Featured Pro Plus Badge', starter: false, pro: true, elite: 'Elite Badge' },
                    { feature: 'Multi-Location Profiles', starter: false, pro: false, elite: true },
                    { feature: 'Service Area Mapping', starter: 'Basic', pro: 'Advanced', elite: 'Advanced' },
                    { feature: 'Dedicated Account Manager', starter: false, pro: false, elite: true },
                    { feature: 'Support', starter: 'Email', pro: 'Priority', elite: 'Premium' },
                    { feature: 'Early Access to New Features', starter: false, pro: false, elite: true },
                    { feature: 'Trust Score Display', starter: true, pro: true, elite: true },
                    { feature: 'All Badge Eligibility', starter: true, pro: true, elite: true },
                    { feature: 'Verified Customer Reviews', starter: true, pro: true, elite: true },
                  ].map((row, i) => (
                    <tr key={row.feature} className={`border-b border-gray-100 ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                      <td className="p-4 text-sm font-medium text-gray-800">{row.feature}</td>
                      {[row.starter, row.pro, row.elite].map((val, j) => (
                        <td key={j} className={`text-center p-4 ${j === 1 ? 'bg-[#E07A5F]/[0.02]' : j === 2 ? 'bg-amber-50/30' : ''}`}>
                          {val === true ? (
                            <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                          ) : val === false ? (
                            <span className="text-gray-300">—</span>
                          ) : (
                            <span className="text-sm font-medium text-gray-700">{val}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* The Link-A-Pro Trust System */}
          <div className="mb-20">
            <div className="bg-[#0F5156] rounded-3xl p-8 md:p-12 text-white">
              <div className="max-w-3xl mx-auto text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-white/10 flex items-center justify-center">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-6">
                  The Link-A-Pro Trust System
                </h3>
                <p className="text-lg opacity-90 mb-8">
                  Our commitment to the people of Guyana is simple:
                </p>
                <div className="space-y-3 text-left max-w-xl mx-auto mb-8">
                  {[
                    'No professional can pay for higher rankings.',
                    'No professional can pay for better ratings.',
                    'No professional can buy a Top Rated badge.',
                    'No professional can buy a Guyana Trusted badge.',
                    'No professional can buy an Elite Pro badge.',
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
                      <X className="w-5 h-5 text-[#E07A5F] flex-shrink-0" />
                      <span className="font-medium">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-3 text-lg opacity-90">
                  <p>Every review comes from verified customers who completed real jobs through the platform.</p>
                  <p className="font-semibold">Every badge is earned. Every Trust Score is independently calculated.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Badges That Cannot Be Purchased */}
          <div className="mb-20">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-3">Badges That Cannot Be Purchased</h3>
            <p className="text-gray-500 text-center mb-10 text-sm">All badges are earned through performance — never purchased.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { icon: Shield, color: 'bg-blue-50 text-blue-600 border-blue-100', title: 'Verified Pro', desc: 'Identity and contact information verified.' },
                { icon: GraduationCap, color: 'bg-purple-50 text-purple-600 border-purple-100', title: 'Certified Professional', desc: 'Professional licenses and certifications verified.' },
                { icon: Star, color: 'bg-amber-50 text-amber-600 border-amber-100', title: 'Top Rated Pro', desc: 'Consistently excellent customer ratings.' },
                { icon: Zap, color: 'bg-cyan-50 text-cyan-600 border-cyan-100', title: 'Fast Responder', desc: 'Quick response times to customer inquiries.' },
                { icon: Flag, color: 'bg-green-50 text-green-600 border-green-100', title: 'Guyana Trusted', desc: 'Long-term reliability and exceptional customer satisfaction.' },
                { icon: Trophy, color: 'bg-orange-50 text-orange-600 border-orange-100', title: 'Elite Pro', desc: 'Top 5% of professionals in their category based on Trust Score and performance.' },
              ].map((badge) => (
                <div key={badge.title} className={`rounded-2xl p-5 border ${badge.color.split(' ')[0]} ${badge.color.split(' ').slice(2).join(' ')}`}>
                  <div className={`w-10 h-10 rounded-lg ${badge.color.split(' ')[0]} flex items-center justify-center mb-3`}>
                    <badge.icon className={`w-5 h-5 ${badge.color.split(' ')[1]}`} />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-1">{badge.title}</h4>
                  <p className="text-gray-600 text-sm">{badge.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Pro Plus Clarification */}
          <div className="mb-20">
            <div className="bg-white rounded-2xl border border-gray-200 p-8 md:p-10">
              <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-[#E07A5F]/10 flex items-center justify-center">
                    <Award className="w-5 h-5 text-[#E07A5F]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Pro Plus Membership Benefits</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Professionals subscribed to Pro or Elite plans receive the Pro Plus designation and enhanced presentation features such as highlighted profile cards, featured placements, and additional business tools.
                </p>
                <p className="font-semibold text-gray-900 mb-4">However:</p>
                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                  {[
                    'Pro Plus DOES NOT improve Trust Score.',
                    'Pro Plus DOES NOT improve ratings.',
                    'Pro Plus DOES NOT improve search rankings.',
                    'Pro Plus DOES NOT influence badge eligibility.',
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 bg-red-50 rounded-lg p-3 border border-red-100">
                      <X className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-600 text-sm mb-4">Search results remain independently sorted using:</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Trust Score', 'Verified Reviews', 'Completed Jobs', 'Customer Recommendation Rate', 'Response Time'].map((criterion, i) => (
                    <span key={criterion} className="inline-flex items-center gap-1 bg-[#0F5156]/5 text-[#0F5156] rounded-full px-3 py-1 text-sm font-medium">
                      <span className="text-xs text-gray-400">{i + 1}.</span>
                      {criterion}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-gray-600 italic">
                  This ensures that the most trusted professionals rise to the top based on merit, not advertising spend.
                </p>
              </div>
            </div>
          </div>

          {/* Our Promise to Guyana */}
          <div className="text-center">
            <div className="max-w-2xl mx-auto">
              <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-[#0F5156]/10 flex items-center justify-center">
                <Flag className="w-7 h-7 text-[#0F5156]" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Promise to Guyana</h3>
              <p className="text-gray-600 mb-6">
                Link-A-Pro was built to solve one of Guyana's biggest challenges: finding professionals you can trust.
              </p>
              <div className="flex flex-wrap justify-center gap-3 mb-6">
                {['Reward quality work.', 'Reward professionalism.', 'Reward reliability.', 'Reward honesty.'].map((promise) => (
                  <span key={promise} className="inline-flex items-center gap-2 bg-[#0F5156]/5 rounded-full px-4 py-2 text-sm font-medium text-[#0F5156]">
                    <CheckCircle className="w-4 h-4" />
                    {promise}
                  </span>
                ))}
              </div>
              <p className="text-xl font-bold text-[#0F5156] mb-8">Because trust should never be for sale.</p>
              <a 
                href="https://app.link-a-pro.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#0F5156] hover:bg-[#0A3A3E] text-white rounded-xl px-8 py-4 text-lg font-medium transition-all duration-300 hover:-translate-y-1 shadow-lg shadow-[#0F5156]/25"
                data-testid="plans-cta"
              >
                <Smartphone className="w-5 h-5" />
                Join Link-A-Pro Today
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Document Requirements — Transparency */}
      <section id="documents" className="py-20 md:py-32 bg-white border-t border-gray-100" data-testid="documents-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#E07A5F] font-semibold text-sm tracking-wider uppercase">Verification &amp; Transparency</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-4">
              What Professionals Submit to Get Verified
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Every plan tier requires real documents. Customers can hire with confidence knowing exactly what each Pro has submitted — and we publish the full list so there are no surprises.
            </p>
          </div>

          {/* Legend */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-8 max-w-4xl mx-auto" data-testid="documents-legend">
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-100 text-green-700 font-bold text-xs">R</span>
                <span className="text-gray-700"><strong>Required</strong> — account blocked without this</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-orange-100 text-orange-700 font-bold text-xs">O</span>
                <span className="text-gray-700"><strong>Optional but recommended</strong> (shown on profile)</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold text-xs">C</span>
                <span className="text-gray-700"><strong>Conditional</strong> — required only for relevant categories</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-400 font-bold text-xs">—</span>
                <span className="text-gray-700"><strong>Not applicable</strong> at this tier</span>
              </div>
            </div>
          </div>

          {/* Document Requirements Table */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="documents-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-200 bg-gray-50/50">
                  <tr>
                    <th className="text-left px-6 py-5 text-xs font-bold tracking-wider text-gray-500 uppercase">Document</th>
                    <th className="px-6 py-5 text-center">
                      <div className="inline-flex flex-col items-center gap-1">
                        <span className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold tracking-wider">STARTER</span>
                        <span className="text-[10px] text-gray-500 font-medium tracking-wider">FREE / ENTRY PLAN</span>
                      </div>
                    </th>
                    <th className="px-6 py-5 text-center">
                      <div className="inline-flex flex-col items-center gap-1">
                        <span className="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-bold tracking-wider">PRO</span>
                        <span className="text-[10px] text-gray-500 font-medium tracking-wider">PAID PLAN</span>
                      </div>
                    </th>
                    <th className="px-6 py-5 text-center">
                      <div className="inline-flex flex-col items-center gap-1">
                        <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold tracking-wider">ELITE</span>
                        <span className="text-[10px] text-gray-500 font-medium tracking-wider">PREMIUM / TOP-TIER</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { name: 'Guyana National ID',           note: 'Front side, clear photo or scan',           code: 'national_id',     starter: 'R', pro: 'R', elite: 'R' },
                    { name: "Driver's Licence",             note: 'Front side, all 4 corners visible',         code: 'drivers_license', starter: 'O', pro: 'R', elite: 'R' },
                    { name: 'Tax Identification (TIN)',     note: 'TIN slip or letter',                        code: 'tax_tin',         starter: 'O', pro: 'R', elite: 'R' },
                    { name: 'Trade Certification',          note: 'Skills certificate from a recognised body', code: 'trade_cert',      starter: '—', pro: 'R', elite: 'R' },
                    { name: 'Trade Licence / Permit',       note: 'Issued by municipality',                    code: 'trade_license',   starter: '—', pro: 'O', elite: 'R' },
                    { name: 'Business Registration Certificate', note: 'Full certificate, all pages',           code: 'business_reg',   starter: '—', pro: 'O', elite: 'R' },
                    { name: 'Public Liability Insurance',   note: 'Cover page + first schedule',               code: 'public_liability',starter: '—', pro: '—', elite: 'R' },
                    { name: 'Vehicle Registration',         note: 'Movers, transport & contractors with vehicles', code: 'vehicle_reg', starter: '—', pro: '—', elite: 'C' },
                  ].map((doc, idx) => (
                    <tr key={doc.code} className={idx % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/40 hover:bg-gray-50'} data-testid={`doc-row-${doc.code}`}>
                      <td className="px-6 py-5">
                        <div className="font-semibold text-gray-900 mb-1">{doc.name}</div>
                        <div className="text-sm text-gray-500">
                          {doc.note} · <code className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded text-xs font-mono">{doc.code}</code>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center"><DocBadge value={doc.starter} /></td>
                      <td className="px-6 py-5 text-center"><DocBadge value={doc.pro} /></td>
                      <td className="px-6 py-5 text-center"><DocBadge value={doc.elite} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mt-8 max-w-3xl mx-auto">
            <Shield className="w-4 h-4 inline-block mr-1 text-[#0F5156]" />
            All documents are reviewed by Link-A-Pro admins. Profiles display only the badges earned through verified submissions — never assumed, never paid.
          </p>
        </div>
      </section>


      {/* Contact Section */}
      <section id="contact" className="py-20 md:py-32 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <span className="text-[#E07A5F] font-semibold text-sm tracking-wider uppercase">Get In Touch</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Contact Us
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Have questions or want to partner with us? We'd love to hear from you.
            </p>
            
            <form onSubmit={handleContactSubmit} className="space-y-6 text-left" data-testid="contact-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <Input 
                  type="text"
                  placeholder="Your name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="h-12"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <Input 
                  type="email"
                  placeholder="your@email.com"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="h-12"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <Textarea 
                  placeholder="How can we help you?"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  rows={5}
                  data-testid="contact-message"
                />
              </div>
              <Button 
                type="submit"
                disabled={submitting}
                className="w-full bg-[#0F5156] hover:bg-[#0A3A3E] text-white rounded-full py-6 text-lg font-medium"
                data-testid="contact-submit"
              >
                {submitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-6">
                <img src="/logo.png" alt="Link-A-Pro" className="h-10 w-auto brightness-0 invert" />
              </div>
              <p className="text-gray-400 mb-6 max-w-md">
                Connecting homeowners with trusted local professionals. Get your home projects done right.
              </p>
              
              <div className="flex gap-4">
                <a 
                  href="https://app.link-a-pro.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#0F5156] hover:bg-[#0A3A3E] px-5 py-3 rounded-lg transition-colors"
                  data-testid="footer-launch-app"
                >
                  <Smartphone className="w-5 h-5" />
                  <span className="text-sm font-medium">Open App</span>
                </a>
                <a 
                  href="https://app.link-a-pro.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-5 py-3 rounded-lg transition-colors"
                  data-testid="footer-join-pro"
                >
                  <Crown className="w-5 h-5" />
                  <span className="text-sm">Join as Pro</span>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors" data-testid="footer-about">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors" data-testid="footer-careers">Careers</a></li>
                <li><Link to="/support" className="hover:text-white transition-colors" data-testid="footer-support">Support</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-3 text-gray-400">
                <li><Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
                <li><Link to="/support" className="hover:text-white transition-colors" data-testid="footer-safety">Safety & Trust</Link></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-white transition-colors" data-testid="footer-contact">Contact</button></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>© {new Date().getFullYear()} Link-A-Pro. All rights reserved.</p>
            <p className="mt-2">Powered by <span className="text-[#E07A5F] font-medium">The TechXpert</span></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
